// Phase 11 Step 12 — OwlShareCard.
//
// Branded social sharing card for Owl Hour wins. Dark gradient
// background, owl emoji, tier name, win amount large, STRIKZA
// branding. Designed for screenshot sharing.
//
// Share button uses native Web Share API if available,
// fallback to "Copy to clipboard" concept.

import type { OwlUserSlip } from "../../owl/useOwlHourUserSlips.ts";
import type { OwlHourSlip } from "../../owl/useOwlHourSlips.ts";
import { OWL_TIER_META } from "../../owl/useOwlHourSlips.ts";
import * as s from "./owl.styles.ts";

// ─── Formatters ───────────────────────────────────────────────

function fmtCurrency(n: number): string {
  return `GH₵${n.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(raw: string): string {
  if (raw.length === 0) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Component ────────────────────────────────────────────────

export interface OwlShareCardProps {
  userSlip: OwlUserSlip;
  slip: OwlHourSlip;
}

export function OwlShareCard({ userSlip, slip }: OwlShareCardProps): JSX.Element {
  const meta = OWL_TIER_META[slip.tier];
  const returnAmt = userSlip.actual_return ?? userSlip.potential_return ?? 0;

  return (
    <div style={s.shareCard}>
      <span style={s.shareBrand}>STRIKZA · OWL HOUR</span>

      <span style={{ fontSize: "48px", lineHeight: 1 }}>🦉</span>

      <span style={s.shareAmount}>{fmtCurrency(returnAmt)}</span>

      <span style={s.shareTier}>{meta.label}</span>

      <span style={{
        fontFamily: "var(--sz-font-body)",
        fontSize: "var(--sz-body-sm)",
        color: "var(--owl-text-2)",
        lineHeight: 1.5,
        maxWidth: 320,
      }}>
        I staked {fmtCurrency(userSlip.stake)} on the STRIKZA {meta.label}.
        Woke up to {fmtCurrency(returnAmt)}.
      </span>

      {slip.combined_odds !== null ? (
        <span style={{
          fontFamily: "var(--sz-font-mono)",
          fontSize: "var(--sz-mono-md)",
          color: "var(--owl-violet)",
        }}>
          {slip.combined_odds.toFixed(1)}x · {userSlip.legs_correct ?? 0}/{userSlip.legs_total ?? 0} legs
        </span>
      ) : null}

      <span style={{
        fontFamily: "var(--sz-font-mono)",
        fontSize: "var(--sz-mono-xs)",
        color: "var(--owl-text-3)",
        letterSpacing: "0.06em",
      }}>
        {fmtDate(slip.slip_date)}
      </span>
    </div>
  );
}
